import { useCapabilities } from './CapabilityProvider';
import NotAuthorized from './NotAuthorized';
import NotAuthenticated from './NotAuthenticated';
import PermissionLoadError from './PermissionLoadError';
import AdminSpinner from './ui/AdminSpinner';
import { checkCapabilityMismatch } from '../../utils/adminAuthSmokeChecks';

/**
 * RequireCapability Route Guard
 * 
 * Wraps a route and ensures the user has the required capability.
 * Distinguishes between 401 (unauthenticated) and 403 (unauthorized).
 * 
 * @param {object} props
 * @param {string} props.capability - Required capability (e.g., 'admin:audit:read')
 * @param {React.ReactNode} props.children - Child components to render if authorized
 */
export default function RequireCapability({ capability, children }) {
  const { loading, error, has, isUnauthenticated, isAuthenticated, me } = useCapabilities();
  
  // Debug logging (only when ?debug=1)
  const urlParams = new URLSearchParams(window.location.search);
  const isDebug = urlParams.get('debug') === '1';
  
  if (isDebug && me) {
    console.log('[RequireCapability] Debug:', {
      capability,
      isSuperAdmin: me.isSuperAdmin,
      capabilityCount: me.capabilities?.length || 0,
      hasCapability: has(capability),
      capabilities: me.capabilities?.slice(0, 5) || [],
    });
  }

  // Show loading state while fetching capabilities
  if (loading) {
    return (
      <div style={styles.loading}>
        <AdminSpinner size="md" />
        <p style={{ marginTop: 'var(--spacing-2)', color: 'var(--color-text-tertiary)' }}>Loading...</p>
      </div>
    );
  }

  // If unauthenticated (401), show Sign In screen
  if (isUnauthenticated || (error && (error.status === 401 || error.code === 'UNAUTHENTICATED'))) {
    return <NotAuthenticated />;
  }

  if (error || !isAuthenticated) {
    return <PermissionLoadError />;
  }

  // SUPER ADMIN BYPASS: If user is super admin, always allow access
  // This ensures super admins never get blocked by client-side capability checks
  if (isAuthenticated && me?.isSuperAdmin === true) {
    if (isDebug) {
      console.log('[RequireCapability] Super admin bypass engaged - allowing access');
    }
    return <>{children}</>;
  }

  // If authenticated but lacks capability, show Access Denied (403)
  if (isAuthenticated && !has(capability)) {
    // Smoke check: verify this is not a false negative
    checkCapabilityMismatch(capability, has, true, me);
    return <NotAuthorized requiredCapability={capability} />;
  }

  // User has capability, render children
  return <>{children}</>;
}

const styles = {
  loading: {
    padding: 'var(--spacing-8)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-2)',
  },
  error: {
    padding: 'var(--spacing-8)',
    backgroundColor: 'var(--color-semantic-error-bg)',
    color: 'var(--color-semantic-error-text)',
    borderRadius: 'var(--radius-md)',
  },
};

